import type { TimeZone } from '@libs/enums';
import { RestClient } from '@libs/rest-client';
import type { Locale } from '@ps-smart-cp/ppfw-client';
import { LocalizedContentsProvider, useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

type Region = 'fa';

interface AvailableCompany {
  code: string;
  name: string;
  availableRegions: Region[];
}

interface Organization {
  id: string;
  name: string;
}

interface RootUser {
  type: 'ROOT';
  email: string;
  name: string;
  organization: Organization;
  locale: Locale;
  availableCompanies: AvailableCompany[];
  timeZone: TimeZone;
}

interface IamUser {
  type: 'IAM';
  id: number;
  name: string;
  username: string;
  email?: string;
  organization: Organization;
  locale: Locale;
  isAdmin: boolean;
  availableCompanies: AvailableCompany[];
  timeZone: TimeZone;
}

type User = IamUser | RootUser;

interface UserContextProps {
  user: User | null;
  fetchUserCtx: () => Promise<void>;
  reloadApp: () => void;
}

const UserContext = createContext<UserContextProps>({
  user: null,
  fetchUserCtx: async () => {},
  reloadApp: () => {},
});

function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [key, setKey] = useState(0);

  const { tryRequest } = useTry();

  const fetchUserCtx = async () => {
    await tryRequest(async () => {
      const res = await RestClient.auth.get<User>('/me');
      setUser(res);

      const isPendoEnabledEnv = ['staging', 'production'].includes(process.env.ENV);

      if (!window.pendo || !process.env.PENDO_API_KEY || !isPendoEnabledEnv) {
        return;
      }

      const email = res.email ?? '';
      window.pendo.initialize({
        visitor: {
          id: res.type === 'ROOT' ? email : `${res.organization.name}-${res.username}-${res.id}`,
          email,
          name: res.name,
          app_language: 'ja',
        },
        account: {
          id: res.organization.id,
          name: res.organization.name,
        },
      });
    });
  };

  useEffect(() => {
    void fetchUserCtx();
  }, []);

  return user ? (
    <UserContext.Provider
      key={key}
      value={{
        user,
        fetchUserCtx,
        reloadApp: async () => {
          await fetchUserCtx();
          setKey((pre) => pre + 1);
        },
      }}
    >
      <LocalizedContentsProvider locale={user.locale}>{children}</LocalizedContentsProvider>
    </UserContext.Provider>
  ) : null;
}

function useUser() {
  return useContext(UserContext);
}

export type { IamUser, Region, RootUser, User };
export { UserProvider, useUser };
